import React, { useContext } from 'react';
import { ISubcategoryContext, SubcategoryContext } from '../../../subcategoryState/subcategoryContext';
import { UserRoles, ISubcategoryByCustomerV2 } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import { useLoggedUserRestaurants } from 'hooks/useLoggedUserRestaurants';
import Text from 'components/Text/Text';
import { ImgUploaderByPreSignedUrl } from 'components/ImgUploader/ImgUploaderByPreSignedUrl';
import { RestaurantsAsLinkList } from 'components/Lists/ListView/RestaurantsAsLinkList';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import style from './Item.module.scss';

interface IProps {
	data: ISubcategoryByCustomerV2['items'][0];
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion, setIDForAssignRestaurants } = useContext<ISubcategoryContext>(SubcategoryContext);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const subcategoryRowClasses = classNames(style.subcategory, style.row);
	const subcategoryNameClasses = classNames(style.subcategoryName);
	const categoryNameRowClasses = classNames(style.categoryName, style.row);
	const numberOfRestaurantsRowClasses = classNames(style.restaurant, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const restaurants = data.restaurants.map((restaurant) => ({ id: restaurant.id, restaurantName: restaurant.name }));
	const accessibleRestaurants = useLoggedUserRestaurants(restaurants);

	const editButtonElement = accessibleRestaurants.length ? (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/subcategory/edit?subcategoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsList" size="s" />
			</div>
			<Link to={`/menu-v2/subcategory/edit-restaurant-properties?subcategoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
		</div>
	) : (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/subcategory/edit?subcategoryID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="assignRestaurants" size="s" />
			</div>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<div className={subcategoryRowClasses}>
					<div className={style.imgWrapper}>
						<ImgUploaderByPreSignedUrl loaderSize="small" preSignedUrl={data.preSignedUrl} />
					</div>
					<Text
						className={subcategoryNameClasses}
						translationKey={data.name}
						withTooltip={true}
						data-test="subcategoryName"
					/>
				</div>
				<Text
					className={categoryNameRowClasses}
					translationNameSpace="form"
					translationKey={data.category.name}
					withTooltip={true}
					data-test="categoryName"
				/>
				<div className={numberOfRestaurantsRowClasses}>
					<RestaurantsAsLinkList restaurants={accessibleRestaurants} />
				</div>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/subcategory/view?subcategoryID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
